import { useCallback, useEffect, useMemo, useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import {
  type DocumentTemplateDto,
  type DocumentTemplateListResponse,
  type RenderFormatT,
} from '@toga/shared'

interface Props {
  opportunityId: string
  onClose: () => void
  onApplied: () => void | Promise<void>
}

export default function ApplyTemplateModal({ opportunityId, onClose, onApplied }: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [templates, setTemplates] = useState<DocumentTemplateDto[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [preview, setPreview] = useState('')
  const [format, setFormat] = useState<RenderFormatT>('pdf')
  const [fileName, setFileName] = useState('')
  const [loading, setLoading] = useState(true)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<DocumentTemplateListResponse>('/document-templates')
      .then((r) => {
        setTemplates(r.items)
        if (r.items.length > 0) setSelectedId(r.items[0]!.id)
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error'))
      .finally(() => setLoading(false))
  }, [])

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId]
  )

  // Render preview con datos reales de la oportunidad.
  const reloadPreview = useCallback(async () => {
    if (!selectedId) return
    setPreviewLoading(true)
    try {
      const r = await api.post<{ rendered: string }>(
        `/document-templates/${selectedId}/preview`,
        { opportunityId }
      )
      setPreview(r.rendered)
    } catch (err) {
      setPreview(t('applyTemplate.previewError'))
      void err
    } finally {
      setPreviewLoading(false)
    }
  }, [selectedId, opportunityId, t])

  useEffect(() => {
    void reloadPreview()
    if (selected) {
      setFileName(`${selected.name.replace(/[^a-zA-Z0-9._\-\s]/g, '').trim() || 'documento'}.${format}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  useEffect(() => {
    // Cuando cambia el formato, actualizamos la extensión.
    if (!fileName) return
    const noExt = fileName.replace(/\.(md|pdf|docx)$/i, '')
    setFileName(`${noExt}.${format}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format])

  const onApply = async () => {
    if (!selectedId) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/document-templates/${selectedId}/apply`, {
        opportunityId,
        format,
        fileName: fileName.trim() || undefined,
      })
      await onApplied()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('applyTemplate.title')} maxWidth="max-w-4xl">
      {loading ? (
        <div className="t-muted flex items-center gap-2 py-6">
          <Loader2 size={14} className="animate-spin" /> {t('applyTemplate.loadingTemplates')}
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-8">
          <FileText size={28} className="mx-auto t-faint mb-2" />
          <p className="t-muted text-sm">{t('applyTemplate.noTemplates')}</p>
          <a href="/settings/templates" className="text-toga-700 dark:text-toga-300 hover:underline text-sm">
            {t('applyTemplate.createFirstLink')}
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-[200px_1fr] gap-3">
          {/* Lista */}
          <aside className="border border-base rounded p-2 max-h-[500px] overflow-y-auto">
            <ul className="space-y-0.5">
              {templates.map((tpl) => (
                <li key={tpl.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(tpl.id)}
                    className={[
                      'w-full text-left px-2 py-1.5 rounded text-sm',
                      tpl.id === selectedId
                        ? 'bg-toga-500/15 text-toga-700 dark:text-toga-200 border border-toga-500/30'
                        : 't-secondary hover:bg-hover border border-transparent',
                    ].join(' ')}
                  >
                    <div className="font-medium truncate">{tpl.name}</div>
                    <div className="text-[10px] t-faint">
                      {t('enums:DocumentTemplateCategory.' + tpl.category)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Preview + controls */}
          <div className="space-y-3 flex flex-col">
            <div className="border border-base rounded p-3 bg-[var(--bg-input-soft)] max-h-[400px] overflow-y-auto">
              <div className="text-[10px] uppercase tracking-wider t-faint mb-2 flex items-center gap-2">
                {t('applyTemplate.previewHeader')}
                {previewLoading && <Loader2 size={10} className="animate-spin" />}
              </div>
              <div className="prose prose-sm prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview}</ReactMarkdown>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs t-muted">{t('applyTemplate.format')}</span>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as RenderFormatT)}
                  className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
                >
                  <option value="pdf">{t('applyTemplate.formatPdf')}</option>
                  <option value="docx">{t('applyTemplate.formatDocx')}</option>
                  <option value="md">{t('applyTemplate.formatMd')}</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs t-muted">{t('applyTemplate.fileName')}</span>
                <input
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 font-mono"
                />
              </label>
            </div>

            {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void onApply()}
                disabled={busy || !selectedId}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {t('applyTemplate.createDocument')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
